import { useEffect, useRef } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import {
  FirebaseMessaging,
  type NotificationActionPerformedEvent,
  type NotificationReceivedEvent,
  type TokenReceivedEvent,
} from '@capacitor-firebase/messaging';
import { useQueryClient } from '@tanstack/react-query';
import { env } from '@config/env';
import { navigateTo } from '@shared/lib/navigation';
import { useAuthStore } from '@shared/stores/authStore';
import { useSettingsStore } from '@shared/stores/settingsStore';
import {
  markNotificationRead,
  registerPushToken,
  type NotificationType,
} from './api';
import { rememberToken, unregisterStoredToken } from './pushToken';
import { routeNotification } from './routeNotification';
import { useTenantSwitchPrompt } from './tenantSwitchPromptStore';

const APP_ID = 'ip4estates';

/**
 * Native push bridge — uses @capacitor-firebase/messaging so the token we register
 * is a real **FCM registration token** on BOTH platforms. The plain
 * @capacitor/push-notifications plugin returns the raw APNs token on iOS, which the
 * FCM-based backend (firebase-admin) rejects — so token acquisition must go through
 * Firebase Messaging (it exchanges the APNs token for an FCM token via the SDK that
 * AppDelegate's FirebaseApp.configure() sets up).
 */
export function NativePushBridge(): null {
  const queryClient = useQueryClient();
  const isAuthed = useAuthStore((state) => state.isAuthenticated());
  const tenantId = useAuthStore((state) => state.tenantId);
  const pushEnabled = useSettingsStore((state) => state.pushEnabled);
  const lastRegisteredToken = useRef<string | null>(null);

  useEffect(() => {
    // Only on a native build whose env enables push, for an authenticated tenant user.
    if (!env.push?.enabled || !Capacitor.isNativePlatform() || !isAuthed || !tenantId) return;

    let cancelled = false;
    const handles: PluginListenerHandle[] = [];

    const addListenersAndRegister = async (): Promise<void> => {
      if (!pushEnabled) {
        lastRegisteredToken.current = null;
        await unregisterStoredToken();
        return;
      }

      const permission = await ensurePushPermission();
      useSettingsStore.getState().setPushEnabled(permission === 'granted');
      if (permission !== 'granted') {
        lastRegisteredToken.current = null;
        await unregisterStoredToken();
        return;
      }
      if (cancelled) return;

      // FCM token refreshes (and the initial token on some platforms) arrive here.
      handles.push(
        await FirebaseMessaging.addListener('tokenReceived', (event: TokenReceivedEvent) => {
          void onToken(event.token);
        }),
      );
      handles.push(
        await FirebaseMessaging.addListener('notificationReceived', (event: NotificationReceivedEvent) => {
          onNotificationReceived(event);
        }),
      );
      handles.push(
        await FirebaseMessaging.addListener('notificationActionPerformed', (event: NotificationActionPerformedEvent) => {
          void onNotificationActionPerformed(event);
        }),
      );

      // Explicitly fetch the current FCM token (on iOS this triggers APNs registration
      // and exchanges the APNs token for an FCM token via the Firebase SDK).
      try {
        const { token } = await FirebaseMessaging.getToken();
        if (!cancelled) await onToken(token);
      } catch (error) {
        console.warn('[push] getToken failed:', error);
      }
    };

    const onToken = async (rawToken: string | null | undefined): Promise<void> => {
      const value = rawToken?.trim();
      if (!value || value === lastRegisteredToken.current) return;

      await registerPushToken({
        token: value,
        platform: Capacitor.getPlatform(),
        app_id: APP_ID,
      });
      lastRegisteredToken.current = value;
      rememberToken(value);
    };

    const onNotificationReceived = (event: NotificationReceivedEvent): void => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      console.info('[push] notification received:', event.notification?.title);
    };

    const onNotificationActionPerformed = async (event: NotificationActionPerformedEvent): Promise<void> => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });

      const data = (event.notification?.data ?? {}) as Record<string, unknown>;
      const str = (key: string): string | undefined =>
        typeof data[key] === 'string' && (data[key] as string).trim() ? (data[key] as string) : undefined;

      const notificationId = str('notification_id') ?? null;
      if (notificationId) {
        await markNotificationRead(notificationId).catch(() => { /* non-fatal */ });
      }

      let metadata: Record<string, unknown> | null = null;
      const ctx = str('context');
      if (ctx) {
        try { metadata = JSON.parse(ctx) as Record<string, unknown>; } catch { metadata = null; }
      }

      const auth = useAuthStore.getState();
      routeNotification(
        {
          action_url: str('action_url'),
          notification_type: normalizeNotificationType(data.notification_type),
          related_id: str('related_id') ?? str('entity_id'),
          related_entity_type: str('entity_type'),
          metadata,
          tenant_id: str('tenant_id'),
        },
        {
          activeTenantId: auth.tenantId,
          linkedTenants: auth.linkedTenants,
          navigate: (path) => navigateTo(path),
          requestSwitch: useTenantSwitchPrompt.getState().request,
          openExternal: (url) => {
            void Browser.open({ url }).catch(() => {
              window.open(url, '_blank', 'noopener,noreferrer');
            });
          },
        },
      );
    };

    void addListenersAndRegister().catch((error) => {
      console.warn('[push] setup failed:', error);
    });

    return () => {
      cancelled = true;
      // A new member or community on this device must register again, even with the same FCM token.
      lastRegisteredToken.current = null;
      for (const handle of handles) {
        void handle.remove();
      }
    };
  }, [isAuthed, pushEnabled, queryClient, tenantId]);

  return null;
}

async function ensurePushPermission(): Promise<'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'> {
  const current = await FirebaseMessaging.checkPermissions();
  if (current.receive === 'prompt' || current.receive === 'prompt-with-rationale') {
    const requested = await FirebaseMessaging.requestPermissions();
    return requested.receive;
  }
  return current.receive;
}

function normalizeNotificationType(value: unknown): NotificationType {
  return typeof value === 'string' && value.trim()
    ? (value as NotificationType)
    : 'general';
}
